export const EMPLOYEE_TYPES = {
  MANAGER: 'manager',
  STORE_INCHARGE: 'store_incharge',
  DELIVERY_PERSON: 'delivery_person',
  DRIVER: 'driver',
};

const COMMON_ROUTES = ['update-profile', 'home'];

const SUPER_ADMIN_ROUTES = [
  'super-admin',
  'super-admin/admins',
  'super-admin/admins/:id',
  'super-admin/packages',
  'super-admin/payments',
  'super-admin/users',
  'super-admin/courier-partners',
  'super-admin/whatsapp-inbox',
];

const MANAGER_ROUTES = [
  'hub-ops',
  'add-package',
  'packages',
  'print-invoice',
  'scan-package',
  'hub-queue',
  'transfer-third-party',
  'rto',
  'rto-list',
  'scan-out-of-delivery',
  'delivered',
  'delivery-lists',
  'deliveries',
  'to-pays',
  'collections',
  'out-of-delivery-packages',
  'scanned-packages',
  'enquiries',
  'whatsapp-inbox',
  ...COMMON_ROUTES,
];

const STORE_INCHARGE_ROUTES = [
  'hub-ops',
  'scan-package',
  'hub-queue',
  'transfer-third-party',
  'rto',
  'rto-list',
  'scanned-packages',
  ...COMMON_ROUTES,
];

const DELIVERY_PERSON_ROUTES = [
  'scan-out-of-delivery',
  'delivered',
  'delivery-lists',
  'out-of-delivery-packages',
  ...COMMON_ROUTES,
];

const ROUTES_BY_EMPLOYEE_TYPE = {
  [EMPLOYEE_TYPES.MANAGER]: MANAGER_ROUTES,
  [EMPLOYEE_TYPES.STORE_INCHARGE]: STORE_INCHARGE_ROUTES,
  [EMPLOYEE_TYPES.DELIVERY_PERSON]: DELIVERY_PERSON_ROUTES,
};

const DEFAULT_ROUTE_BY_EMPLOYEE_TYPE = {
  [EMPLOYEE_TYPES.MANAGER]: '/home',
  [EMPLOYEE_TYPES.STORE_INCHARGE]: '/home',
  [EMPLOYEE_TYPES.DELIVERY_PERSON]: '/home',
};

export const getAuthContext = () => {
  const role = localStorage.getItem('role');
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const employeeType = user?.employeeType || '';

  return {
    role,
    user,
    employeeType,
    isAdmin: role === 'admin',
    isSuperAdmin: role === 'super_admin',
    isClient: role === 'client',
    isManager: employeeType === EMPLOYEE_TYPES.MANAGER,
  };
};

/** Admin, manager, and super-admin always; other employees only for to-pay packages. */
export const canViewPaymentDetails = (pkg) => {
  const { isAdmin, isSuperAdmin, isManager } = getAuthContext();
  if (isAdmin || isSuperAdmin || isManager) {
    return true;
  }
  return Boolean(pkg?.toPay);
};

/** Package modal actions (edit / open in list) — admin and manager only. */
export const canManagePackageActions = () => {
  const { isAdmin, isManager } = getAuthContext();
  return isAdmin || isManager;
};

const normalizeRoutePath = (path = '') => path.replace(/^\//, '').split('?')[0];

const matchesSuperAdminRoute = (routePath) => {
  if (routePath === 'super-admin' || routePath.startsWith('super-admin/')) {
    return true;
  }
  return SUPER_ADMIN_ROUTES.includes(routePath);
};

const CLIENT_ROUTES = ['client', 'client/bills', 'client/packages', 'client/password'];

const matchesClientRoute = (routePath) =>
  routePath === 'client' || routePath.startsWith('client/');

export const canAccessRoute = (path) => {
  const { isAdmin, isSuperAdmin, isClient, employeeType } = getAuthContext();
  const routePath = normalizeRoutePath(path);

  if (isClient) {
    return matchesClientRoute(routePath) || CLIENT_ROUTES.includes(routePath);
  }

  if (matchesClientRoute(routePath)) {
    return false;
  }

  if (isSuperAdmin) {
    return matchesSuperAdminRoute(routePath);
  }

  if (matchesSuperAdminRoute(routePath)) {
    return false;
  }

  if (isAdmin) {
    return true;
  }

  const allowedRoutes = ROUTES_BY_EMPLOYEE_TYPE[employeeType] || COMMON_ROUTES;
  return allowedRoutes.includes(routePath);
};

export const getDefaultRoute = () => {
  const { isAdmin, isSuperAdmin, isClient, employeeType } = getAuthContext();

  if (isClient) {
    return '/client';
  }

  if (isSuperAdmin) {
    return '/super-admin';
  }

  if (isAdmin) {
    return '/dashboard-admin';
  }

  return DEFAULT_ROUTE_BY_EMPLOYEE_TYPE[employeeType] || '/home';
};

export const filterNavConfig = (navConfig = []) =>
  navConfig
    .map((item) => {
      if (item.children?.length) {
        const filteredChildren = item.children.filter((child) => canAccessRoute(child.path));

        if (filteredChildren.length === 0) {
          return null;
        }

        return { ...item, children: filteredChildren };
      }

      if (!canAccessRoute(item.path)) {
        return null;
      }

      return item;
    })
    .filter(Boolean);
